import { Box, ButtonGroup, ButtonProps, HStack } from '@chakra-ui/react';
import { Link } from '@chakra-ui/next-js';
import { ReactElement, useLayoutEffect, useState } from 'react';
import { ActionButton } from './components/ActionButton';
import { useAppStore } from '~/store';

export interface ActionButtonProps {
  label: string;
  onClick?: (e?: React.MouseEvent) => void;
  href?: string;
  leftIcon?: ReactElement;
  isLoading?: boolean;
  buttonProps?: ButtonProps;
}

export interface ActionButtonsProps {
  primaryAction?: ActionButtonProps;
  secondaryAction?: ActionButtonProps;
  defaultScrolled?: boolean;
}

export const ActionButtons = ({
  primaryAction,
  secondaryAction,
  defaultScrolled = false,
}: ActionButtonsProps) => {
  const [isScrolled, setIsScrolled] = useState(defaultScrolled);
  const isMobile = useAppStore((state) => state.isMobile);

  useLayoutEffect(() => {
    // Sync state when defaultScrolled changes (e.g. navigating between root and search pages
    // reuses the same ActionButtons instance, so useState initial value doesn't re-run).
    // useLayoutEffect runs synchronously before paint, avoiding a one-frame flash.
    setIsScrolled(defaultScrolled || window.scrollY > 150);

    const handleScroll = () => {
      if (defaultScrolled) return;
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [defaultScrolled]);

  if (!primaryAction && !secondaryAction) return null;

  return (
    <Box
      zIndex="6"
      left={isScrolled ? '50%' : undefined}
      position={isScrolled ? 'fixed' : 'relative'}
      transform={isScrolled ? 'translate(-50%, 0)' : 'none'}
      p="0"
      bottom={isMobile ? '80px' : isScrolled ? '16px' : undefined}
      marginLeft={!isScrolled ? 'auto' : undefined}
      // transition="all 0.3s ease-in-out"
      borderRadius={isScrolled ? 'full' : undefined}
      px={isMobile ? 0 : isScrolled ? 4 : 0}
      py={isMobile ? 0 : isScrolled ? 2 : 0}
    >
      <HStack>
        <ButtonGroup>
          {primaryAction &&
            (primaryAction.href ? (
              <Link href={primaryAction.href}>
                <ActionButton
                  onClick={primaryAction.onClick}
                  leftIcon={primaryAction.leftIcon}
                  isLoading={primaryAction.isLoading}
                  placement={isScrolled ? 'floating' : 'header'}
                  {...primaryAction.buttonProps}
                >
                  {primaryAction.label}
                </ActionButton>
              </Link>
            ) : (
              <ActionButton
                onClick={primaryAction.onClick}
                leftIcon={primaryAction.leftIcon}
                isLoading={primaryAction.isLoading}
                placement={isScrolled ? 'floating' : 'header'}
                {...primaryAction.buttonProps}
              >
                {primaryAction.label}
              </ActionButton>
            ))}
          {secondaryAction &&
            (secondaryAction.href ? (
              <Link href={secondaryAction.href}>
                <ActionButton
                  onClick={secondaryAction.onClick}
                  leftIcon={secondaryAction.leftIcon}
                  isLoading={secondaryAction.isLoading}
                  placement={isScrolled ? 'floating' : 'header'}
                  {...secondaryAction.buttonProps}
                >
                  {secondaryAction.label}
                </ActionButton>
              </Link>
            ) : (
              <ActionButton
                onClick={secondaryAction.onClick}
                leftIcon={secondaryAction.leftIcon}
                isLoading={secondaryAction.isLoading}
                placement={isScrolled ? 'floating' : 'header'}
                {...secondaryAction.buttonProps}
              >
                {secondaryAction.label}
              </ActionButton>
            ))}
        </ButtonGroup>
      </HStack>
    </Box>
  );
};
